import { ChartingSectionType, ChartingFailureRelatedIDs, ChartingSectionTypeToTitle } from '@caresend/types';
import { storeToRefs } from 'pinia';
import { defineComponent, computed, ref, reactive, watch, onMounted, onBeforeUnmount, onUnmounted } from 'vue';
import __vue_component__$2 from '../../../../../../buttons/ExpandableComponent/ExpandableComponent.vue.js';
import __vue_component__ from '../../../../../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$3 from '../../../../../../layout/InfoTitle.vue.js';
import __vue_component__$1 from '../../../../../booking/ChartingItemGroup.vue.js';
import { setChartingItems, handleChartingFailureSideEffects, trackChartingItemsAreDone, scrollToFirstHighlightedItem, getAreGroupsHighlightedForSection } from './helpers.js';
import { usePortalStore } from '../../../../store/portal/index.js';
import { CHECK_PENDING_CHARTING_ITEMS_MODAL_VARIANT } from '../../../../store/portal/workflow/items/procedures.js';
import { getConditionalFollowUpDisplayMap, chartingComplete, shouldShowMaybeConditionalItem, initializeHighlightItems } from '../../../../../../../functions/charting.js';

var script = defineComponent({
    name: 'ItineraryWaypointProcedureCharting',
    components: {
        BasicCard: __vue_component__,
        ChartingItemGroup: __vue_component__$1,
        ExpandableComponent: __vue_component__$2,
        InfoTitle: __vue_component__$3,
    },
    model: {
        prop: 'value',
        event: 'update:value',
    },
    props: {
        procedureID: {
            type: String,
            required: true,
        },
        waypointActionID: {
            type: String,
            required: true,
        },
        value: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:value', 'setFailOnComplete', 'setModalMetaVariant'],
    setup(props, { emit, expose }) {
        const portalStore = usePortalStore();
        const procedure = computed(() => portalStore.procedures.getRaw(props.procedureID));
        const chartingItems = computed(() => { var _a, _b, _c; return (_c = (_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.charting) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : []; });
        const { chartingHighlightItems } = storeToRefs(portalStore);
        // Set every time the charting items change and we do a check on the charting items per-section
        const isChartingComplete = ref(false);
        // Group charting items by section. Miscellaneous is the default section when not present
        const chartingItemsBySection = computed(() => {
            const itemsBySection = {
                [ChartingSectionType.CLINICAL_NOTES]: [],
                [ChartingSectionType.DOCUMENTATION]: [],
                [ChartingSectionType.COMPLIANCE]: [],
                [ChartingSectionType.MISCELLANEOUS]: [],
            };
            chartingItems.value.forEach((item, index) => {
                var _a, _b;
                const section = (_a = item.section) !== null && _a !== void 0 ? _a : ChartingSectionType.MISCELLANEOUS;
                if (!itemsBySection[section])
                    itemsBySection[section] = [];
                (_b = itemsBySection[section]) === null || _b === void 0 ? void 0 : _b.push({ item, index });
            });
            return itemsBySection;
        });
        /**
         * Keep track of each group element component so we can later call scrollIntoView on it.
         * Split by section type and the actual shown index (groupIndex) instead of the index in the charting items array.
         */
        const groupElementsBySectionAndIndex = reactive({
            [ChartingSectionType.CLINICAL_NOTES]: {},
            [ChartingSectionType.DOCUMENTATION]: {},
            [ChartingSectionType.COMPLIANCE]: {},
            [ChartingSectionType.MISCELLANEOUS]: {},
        });
        /**
         * Keep track of which group elements should be hightlighted, by section type and group index instead
         * of the index in the charting items array.
         *
         * This is used later to determined which is the first hightlighted item so we can scroll to it.
         */
        const groupIsHighlightedBySectionAndIndex = reactive({
            [ChartingSectionType.CLINICAL_NOTES]: [],
            [ChartingSectionType.DOCUMENTATION]: [],
            [ChartingSectionType.COMPLIANCE]: [],
            [ChartingSectionType.MISCELLANEOUS]: [],
        });
        // Track the completion status of each section
        const isSectionComplete = reactive({
            [ChartingSectionType.CLINICAL_NOTES]: false,
            [ChartingSectionType.DOCUMENTATION]: false,
            [ChartingSectionType.COMPLIANCE]: false,
            [ChartingSectionType.MISCELLANEOUS]: false,
        });
        // Track whether the section is collapsed or expanded.
        const isSectionOpen = reactive({
            [ChartingSectionType.CLINICAL_NOTES]: true,
            [ChartingSectionType.DOCUMENTATION]: true,
            [ChartingSectionType.COMPLIANCE]: true,
            [ChartingSectionType.MISCELLANEOUS]: true,
        });
        // If true, there is only one section in the charting items
        const isSingleSection = ref(null);
        /** serves the purpose of being the "function ref" for each of the group charting items */
        const handleGroupRefAssignment = (sectionType, groupIndex) => (component) => { groupElementsBySectionAndIndex[sectionType][groupIndex] = component !== null && component !== void 0 ? component : null; };
        // If true, highlight the charting items that are still pending
        const shouldHighlight = ref(false);
        /**
         * If charting item id is in the map, then the item should be shown conditionally
         *
         * This is done by checking the conditional follow-ups of the item and its children.
         */
        const conditionalFollowUpDisplayMap = computed(() => {
            const firstLevelMap = getConditionalFollowUpDisplayMap(chartingItems.value.map((item, idx) => ({
                item,
                index: idx,
            })));
            const allChildren = chartingItems.value.flatMap((item) => { var _a; return (_a = item.children) !== null && _a !== void 0 ? _a : []; });
            const childrenMap = getConditionalFollowUpDisplayMap(allChildren.map((item, idx) => ({
                item,
                index: idx,
            })));
            return {
                ...firstLevelMap,
                ...childrenMap,
            };
        });
        // It's usually the first item in the charting items array, but just in case, look for it
        const shouldFail = computed(() => {
            var _a, _b;
            return ((_b = (_a = chartingItems.value) === null || _a === void 0 ? void 0 : _a.find((item) => item.id === ChartingFailureRelatedIDs.CHARTING_FAILURE)) === null || _b === void 0 ? void 0 : _b.selection) === 'No';
        });
        // Helps prevent updating the charting items multiple times in a row after completing charting
        const isUpdateAlreadyScheduled = ref(false);
        /**
         * The exposed function can be called by the specified modal variant so we can
         * highlight any missing items or simply go ahead and complete the charting.
         */
        expose({
            exposedAction: () => {
                /**
                 * If the charting is complete - return whether the procedure should fail or not,
                 * which will complete the workflow item in which RouterModal that calls this function.
                 *
                 * Also, process side effects (procedure failure object) and tracking events
                 */
                if (isChartingComplete.value) {
                    isUpdateAlreadyScheduled.value = true;
                    setChartingItems(props.procedureID, chartingItems.value);
                    handleChartingFailureSideEffects(chartingItems.value, procedure.value, shouldFail.value);
                    trackChartingItemsAreDone(chartingItems.value, props.waypointActionID, props.procedureID);
                    return !shouldFail.value;
                }
                /* Otherwise, highlight the missing items that are still pending and not allowing
                 * the workflow item to be completed.
                 */
                shouldHighlight.value = true;
                scrollToFirstHighlightedItem(groupIsHighlightedBySectionAndIndex, groupElementsBySectionAndIndex);
                return undefined;
            },
        });
        // Initialize highlight items immediately when charting items are available
        const highlightItemsInitialized = ref(false);
        const initializeHighlightItemsIfNeeded = () => {
            if (!highlightItemsInitialized.value
                || (chartingItems.value.length > 0 && chartingHighlightItems.value.length === 0)) {
                portalStore.$state.chartingHighlightItems = initializeHighlightItems(chartingItems.value);
                highlightItemsInitialized.value = true;
            }
            return chartingHighlightItems.value.length > 0;
        };
        const closeSectionDelayed = (sectionType) => window.setTimeout(() => { isSectionOpen[sectionType] = false; }, 400);
        /**
         * Determine if the charting items are all in a single section. dont recalculate if already set
         */
        const maybeInitializeIsSingleSection = () => {
            if (isSingleSection.value !== null)
                return;
            isSingleSection.value = Object.values(chartingItemsBySection.value).some((items) => items.length === chartingItems.value.length);
        };
        const isFirstCompletionCheck = ref(true);
        /**
         * Watch for changes in the charting items by section and update the completion status of
         * each section. If the section goes from incomplete to complete, automatically collapse the section.
         * by updating its corresponding isSectionOpen state.
         */
        watch(chartingItemsBySection, (newChartingItemsBySection) => {
            // Only proceed once the charting highlight items are initialized
            if (!initializeHighlightItemsIfNeeded())
                return;
            maybeInitializeIsSingleSection();
            Object.entries(newChartingItemsBySection).forEach(([section, items]) => {
                const sectionType = section;
                const newComplete = chartingComplete({
                    chartingItems: items,
                    procedureID: props.procedureID,
                });
                const oldComplete = isSectionComplete[sectionType];
                isSectionComplete[sectionType] = newComplete;
                // Close the section after completing if it was open before.
                // If not first time (initialization case), do a small delay so the user can see their work for a moment.
                if (isSingleSection.value)
                    isSectionOpen[sectionType] = true;
                else if (!oldComplete && newComplete) {
                    if (!isFirstCompletionCheck.value)
                        closeSectionDelayed(sectionType);
                    else
                        isSectionOpen[sectionType] = false;
                }
                groupIsHighlightedBySectionAndIndex[sectionType] = getAreGroupsHighlightedForSection(items, chartingHighlightItems.value, conditionalFollowUpDisplayMap.value);
            });
            // Update completion status
            isChartingComplete.value = Object.values(isSectionComplete).every((complete) => complete);
            if (isFirstCompletionCheck.value)
                isFirstCompletionCheck.value = false;
        }, { deep: true, immediate: true });
        // Always set the modal meta variant to show the pending charting items modal
        onMounted(() => emit('setModalMetaVariant', CHECK_PENDING_CHARTING_ITEMS_MODAL_VARIANT));
        // Update the charting items before unmounting to ensure no lost progress - if not already scheduled
        onBeforeUnmount(() => {
            if (!isUpdateAlreadyScheduled.value)
                setChartingItems(props.procedureID, chartingItems.value);
        });
        onUnmounted(() => emit('setModalMetaVariant', undefined));
        return {
            ChartingSectionTypeToTitle,
            chartingItemsBySection,
            conditionalFollowUpDisplayMap,
            groupIsHighlightedBySectionAndIndex,
            handleGroupRefAssignment,
            isSingleSection,
            isSectionComplete,
            isSectionOpen,
            shouldHighlight,
            shouldShowMaybeConditionalItem,
        };
    },
});

export { script as default };
